import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Lock, ShoppingCart, Check, Zap, TrendingUp, DollarSign } from 'lucide-react';
import Logo from '../components/Logo';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);

  const credits = parseInt(searchParams.get('credits')) || 0;
  const amount = (credits * 0.5).toFixed(2);
  const questions = Math.floor(credits / 10);

  const [billingInfo, setBillingInfo] = useState({
    fullName: user?.name || '',
    company: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    taxNumber: ''
  });

  const [acceptContract, setAcceptContract] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.info('Lütfen giriş yapın');
      navigate('/login');
      return;
    }

    if (credits < 10) {
      toast.error('Geçersiz kredi miktarı');
      navigate('/pricing');
      return;
    }
  }, [user, credits, navigate]);

  const handleInputChange = (e) => {
    setBillingInfo({
      ...billingInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptContract) {
      toast.error('Mesafeli satış sözleşmesini kabul etmelisiniz');
      return;
    }

    // Validate required fields
    const requiredFields = ['fullName', 'email', 'phone', 'address', 'city'];
    const missingFields = requiredFields.filter(field => !billingInfo[field]);
    
    if (missingFields.length > 0) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setProcessing(true);

    try {
      // Call PayTR create order endpoint
      const response = await api.post('/paytr/create-order', {
        credits: credits,
        user_name: billingInfo.fullName,
        user_address: `${billingInfo.address}, ${billingInfo.city}`,
        user_phone: billingInfo.phone
      });

      if (response.data.success) {
        // Navigate to payment page with iframe token
        navigate('/payment', {
          state: {
            iframeToken: response.data.iframe_token,
            merchantOid: response.data.merchant_oid,
            amount: response.data.amount_tl,
            credits: response.data.credits,
            mock_mode: response.data.mock_mode || false
          }
        });
      }
    } catch (error) {
      console.error('PayTR order error:', error);
      const errorMsg = error.response?.data?.detail || 'Ödeme oluşturulamadı';
      
      // Check if it's a PayTR configuration error
      if (errorMsg.includes('PayTR') || errorMsg.includes('sandbox')) {
        toast.error('PayTR ödeme sistemi yapılandırması tamamlanmamış. Lütfen daha sonra tekrar deneyin.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (!user || credits < 10) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <Logo />
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-green-600" />
            <span className="text-sm text-slate-600">Güvenli Ödeme</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Sipariş Özeti
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                    <Zap className="h-8 w-8 text-[#FF6000]" />
                    <div>
                      <div className="font-bold text-lg">{credits} Kredi</div>
                      <div className="text-sm text-slate-600">Kredi Paketi</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <TrendingUp className="h-6 w-6 mx-auto mb-1 text-blue-600" />
                      <div className="font-bold text-blue-600">{questions}</div>
                      <div className="text-xs text-slate-600">Soru</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <DollarSign className="h-6 w-6 mx-auto mb-1 text-green-600" />
                      <div className="font-bold text-green-600">{amount} ₺</div>
                      <div className="text-xs text-slate-600">Ücret</div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Kredi Ücreti</span>
                    <span>{amount} ₺</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>KDV (%20)</span>
                    <span>{(amount * 0.2).toFixed(2)} ₺</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Toplam</span>
                    <span className="text-[#FF6000]">{(amount * 1.2).toFixed(2)} ₺</span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Krediler süresiz geçerli</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Güvenli PayTR ödeme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Anında kredi yükleme</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Billing Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Fatura Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Ad Soyad *</Label>
                      <Input
                        name="fullName"
                        value={billingInfo.fullName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label>Şirket/Firma</Label>
                      <Input
                        name="company"
                        value={billingInfo.company}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>E-posta *</Label>
                      <Input
                        type="email"
                        name="email"
                        value={billingInfo.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label>Telefon *</Label>
                      <Input
                        type="tel"
                        name="phone"
                        value={billingInfo.phone}
                        onChange={handleInputChange}
                        placeholder="05XX XXX XX XX"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Adres *</Label>
                    <Input
                      name="address"
                      value={billingInfo.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Şehir *</Label>
                      <Input
                        name="city"
                        value={billingInfo.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label>Vergi No / TC</Label>
                      <Input
                        name="taxNumber"
                        value={billingInfo.taxNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contract */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="contract"
                      checked={acceptContract}
                      onCheckedChange={setAcceptContract}
                    />
                    <Label htmlFor="contract" className="text-sm cursor-pointer">
                      <a href="/terms" target="_blank" className="text-[#FF6000] hover:underline">
                        Mesafeli Satış Sözleşmesi
                      </a>
                      'ni ve{' '}
                      <a href="/privacy" target="_blank" className="text-[#FF6000] hover:underline">
                        Gizlilik Politikası
                      </a>
                      'nı okudum, kabul ediyorum.
                    </Label>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={processing || !acceptContract}
                className="w-full h-14 text-lg bg-[#FF6000] hover:bg-orange-600"
              >
                {processing ? 'Ödeme sayfasına yönlendiriliyor...' : `Ödemeye Geç (${amount} ₺)`}
              </Button>

              <p className="text-center text-sm text-slate-600">
                💳 Güvenli PayTR ödeme sayfasına yönlendirileceksiniz. Ödeme tamamlandığında kredileriniz anında hesabınıza eklenecektir.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
